/**
 * Client-side Firestore API for recurring invoice templates and run history.
 *
 * All reads and writes are owner-scoped (M0-Konform):
 *   - Every template write sets `userId = auth.currentUser.uid`.
 *   - Every query carries `where('userId', '==', uid)`.
 *
 * Collection access:
 *   - `recurring_invoice_templates` — full CRUD for the authenticated owner.
 *   - `recurring_runs`              — READ-ONLY for the authenticated owner.
 *     The PHP backend writes run records via Admin-SDK (bypasses Firestore Rules).
 *     The client must NEVER create or modify run documents.
 *   - `recurring_idempotency`       — backend-only (denied by Rules; not accessed here).
 *
 * NOTE: The automatic invoice generation requires the INFRA-1 scheduler and the
 * `RECURRING_ENABLED=true` environment variable on Cloud Run.  Until Daniel
 * provisions these, the functions below only manage template documents (inert).
 *
 * @module recurringApi
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import {
  RecurringTemplate,
  RecurringRun,
  CreateRecurringTemplateInput,
  UpdateRecurringTemplateInput,
} from '../types/recurringTypes';

const TEMPLATES_COLLECTION = 'recurring_invoice_templates';
const RUNS_COLLECTION = 'recurring_runs';

// ─── Auth guard ───────────────────────────────────────────────────────────────

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous
 * scope). The returned value is the *active tenant scope* via
 * {@link getEffectiveScopeUid}: bit-identical to `auth.currentUser.uid` while the
 * flag is off or the own tenant is active (M11 inertness), and the mandate's
 * `scopeUid` when an advisor has a foreign tenant active.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      'recurringApi: No authenticated user. Sign in before accessing recurring templates.'
    );
  }
  return getEffectiveScopeUid();
}

// ─── Templates ────────────────────────────────────────────────────────────────

/**
 * Creates a new recurring invoice template in `recurring_invoice_templates`.
 *
 * Sets `userId` and `createdBy` to `auth.currentUser.uid` regardless of what
 * the caller supplies (M0 enforcement).  Sets `createdAt` and `updatedAt` via
 * `serverTimestamp()`.
 *
 * @param input - Template fields minus Firestore-managed metadata.
 * @returns Firestore document ID of the newly created template.
 * @throws {Error} If not authenticated or if the Firestore write fails.
 */
export async function createTemplate(
  input: CreateRecurringTemplateInput
): Promise<string> {
  const uid = requireCurrentUser();

  const docData = {
    ...input,
    userId: uid,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const colRef = collection(db, TEMPLATES_COLLECTION);
  const docRef = await addDoc(colRef, docData);
  return docRef.id;
}

/**
 * Loads all recurring templates for the current user, ordered by creation date
 * descending (most recently created first).
 *
 * Requires a Firestore composite index: `userId ASC, createdAt DESC`.
 *
 * @returns Array of `RecurringTemplate` objects (may be empty).
 * @throws {Error} If not authenticated or if the query fails.
 */
export async function getTemplates(): Promise<RecurringTemplate[]> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, TEMPLATES_COLLECTION),
    where('userId', '==', uid),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (d): RecurringTemplate => ({ id: d.id, ...d.data() } as RecurringTemplate)
  );
}

/**
 * Loads a single recurring template by its Firestore document ID.
 *
 * @param templateId - Firestore document ID.
 * @returns The `RecurringTemplate` document.
 * @throws {Error} If not authenticated, if the document does not exist, or
 *                 if the caller does not own the document.
 */
export async function getTemplate(templateId: string): Promise<RecurringTemplate> {
  const uid = requireCurrentUser();

  const docRef = doc(db, TEMPLATES_COLLECTION, templateId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    throw new Error(`recurringApi.getTemplate: Template '${templateId}' not found.`);
  }

  const data = snap.data() as RecurringTemplate;
  if (data.userId !== uid) {
    throw new Error(
      `recurringApi.getTemplate: Access denied — template '${templateId}' is not owned by the current user.`
    );
  }

  // Destructure out any existing `id` field in data to avoid the TS2783
  // "id specified more than once" error when spreading into the return object.
  const { id: _ignored, ...rest } = data;
  return { id: snap.id, ...rest };
}

/**
 * Updates fields on an existing recurring template.
 *
 * Enforces:
 *   - `userId` is NOT patchable via this function.
 *   - `updatedAt` is always refreshed to `serverTimestamp()`.
 *
 * @param templateId - Firestore document ID of the template to update.
 * @param patch - Partial template fields to update.
 * @throws {Error} If not authenticated or if the Firestore update fails.
 */
export async function updateTemplate(
  templateId: string,
  patch: UpdateRecurringTemplateInput
): Promise<void> {
  requireCurrentUser();

  // Explicitly remove userId from any patch to prevent accidental tenant-hijack.
  const { userId: _removedUserId, ...safePatch } = patch as UpdateRecurringTemplateInput & { userId?: string };

  const docRef = doc(db, TEMPLATES_COLLECTION, templateId);
  await updateDoc(docRef, {
    ...safePatch,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Deletes a recurring template document.
 *
 * The Firestore Rule `allow delete: if isOwner(resource.data)` ensures only the
 * owner can delete a template.  The corresponding run history in `recurring_runs`
 * is NOT deleted — runs are append-only records for auditability.
 *
 * @param templateId - Firestore document ID of the template to delete.
 * @throws {Error} If not authenticated or if the delete fails.
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, TEMPLATES_COLLECTION, templateId);
  await deleteDoc(docRef);
}

// ─── Recurring runs (read-only) ───────────────────────────────────────────────

/**
 * Loads the run history for a specific template, ordered by `runAt` descending
 * (most recent first).
 *
 * The client has READ-ONLY access to `recurring_runs`.  The PHP backend writes
 * these documents via Admin-SDK.  The Firestore Rules deny create/update/delete
 * for clients.
 *
 * @param templateId - Firestore document ID of the parent template.
 * @returns Array of `RecurringRun` objects (may be empty if no runs have fired yet).
 * @throws {Error} If not authenticated or if the query fails.
 */
export async function getRecurringRuns(templateId: string): Promise<RecurringRun[]> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, RUNS_COLLECTION),
    where('userId', '==', uid),
    where('templateId', '==', templateId),
    orderBy('runAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (d): RecurringRun => ({ id: d.id, ...d.data() } as RecurringRun)
  );
}
